import { useEffect } from "react";

/**
 * Lightweight per-page SEO hook. Sets <title>, meta description,
 * canonical link and Open Graph title/description/URL without pulling
 * in react-helmet. Values reset when the component unmounts if a
 * fallback was captured on mount.
 */
export default function useSEO({ title, description, path, image } = {}) {
  useEffect(() => {
    const origin = "https://www.rsaoverseas.com";
    const url = path ? `${origin}${path}` : origin;

    const prevTitle = document.title;
    if (title) document.title = title;

    const set = (selector, attr, value) => {
      if (!value) return () => {};
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement(selector.startsWith("meta") ? "meta" : "link");
        // parse selector like meta[name="x"] or meta[property="x"] or link[rel="canonical"]
        const m = selector.match(/\[(name|property|rel)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return () => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute(attr, prev);
      };
    };

    const undo = [
      set('meta[name="description"]', "content", description),
      set('meta[property="og:title"]', "content", title),
      set('meta[property="og:description"]', "content", description),
      set('meta[property="og:url"]', "content", url),
      set('meta[property="og:image"]', "content", image || `${origin}/og-cover.jpg`),
      set('meta[name="twitter:title"]', "content", title),
      set('meta[name="twitter:description"]', "content", description),
      set('meta[name="twitter:image"]', "content", image || `${origin}/og-cover.jpg`),
      set('link[rel="canonical"]', "href", url),
    ];

    return () => {
      document.title = prevTitle;
      undo.forEach((fn) => fn && fn());
    };
  }, [title, description, path, image]);
}
